import { ArrowLeft, ArrowRight, Building2 } from 'lucide-react';
import './styles/PlatformConfiguration.css';

interface Props {
  nextStep: () => void;
  previousStep: () => void;
}

interface HierarchyUser {
  id: string;
  name: string;
  email: string;
  role: 'Admin' | 'User';
  portal: 'Active Portal' | 'Passive Portal';
  initial: string;
  avatarColor: string;
  reportsTo?: string;
  reportsCount?: number;
  children?: HierarchyUser[];
}

const HIERARCHY: HierarchyUser = {
  id: '1',
  name: 'Abdullah AL-Jabri',
  email: 'cys.abdullah@gmail.com',
  role: 'Admin',
  portal: 'Active Portal',
  initial: 'A',
  avatarColor: '#e0f2fe',
  reportsCount: 2,
  children: [
    {
      id: '2',
      name: 'gocera8257',
      email: 'gocera8257@ftpnet.com',
      role: 'User',
      portal: 'Passive Portal',
      initial: 'G',
      avatarColor: '#d1fae5',
      reportsTo: 'Abdullah AL-Jabri',
    },
    {
      id: '3',
      name: 'seyos38194',
      email: 'seyos38194@ftpnet.com',
      role: 'User',
      portal: 'Active Portal',
      initial: 'S',
      avatarColor: '#fce7f3',
      reportsTo: 'Abdullah AL-Jabri',
      reportsCount: 3,
      children: [
        {
          id: '4',
          name: 'lotin92685',
          email: 'lotin92685@koloace.com',
          role: 'Admin',
          portal: 'Passive Portal',
          initial: 'L',
          avatarColor: '#e0e7ff',
          reportsTo: 'seyos38194',
        },
        {
          id: '5',
          name: 'h053a',
          email: 'h053a@deltajohnsons.com',
          role: 'Admin',
          portal: 'Active Portal',
          initial: 'H',
          avatarColor: '#fef3c7',
          reportsTo: 'seyos38194',
        },
      ],
    },
  ],
};

const UserCard = ({ user, isRoot = false }: { user: HierarchyUser; isRoot?: boolean }) => {
  return (
    <div className={`uh-user-card ${isRoot ? 'uh-user-card-root' : ''}`}>
      <div className="uh-user-main">
        <div className="uh-user-left">
          <div className="uh-avatar" style={{ backgroundColor: user.avatarColor }}>
            <span className="uh-avatar-initial">{user.initial}</span>
          </div>
          <div className="uh-user-info">
            <div className="uh-user-name-row">
              <span className="uh-user-name">{user.name}</span>
              <span className={`uh-role-badge ${user.role === 'Admin' ? 'uh-role-admin' : 'uh-role-user'}`}>{user.role}</span>
              <span className={`uh-portal-badge ${user.portal === 'Active Portal' ? 'uh-portal-active' : 'uh-portal-passive'}`}>
                {user.portal}
              </span>
            </div>
            <span className="uh-user-email">{user.email}</span>
            {user.reportsTo && <span className="uh-reports-to">→ Reports to {user.reportsTo}</span>}
          </div>
        </div>
      </div>

      {user.reportsCount != null && user.reportsCount > 0 && (
        <div className="uh-reports-meta">
          <span className="uh-reports-count">↳ {user.reportsCount} reports</span>
          <span className="uh-team-label">TEAM</span>
        </div>
      )}
    </div>
  );
};

const HierarchyBranch = ({ user, depth = 0 }: { user: HierarchyUser; depth?: number }) => {
  const hasChildren = user.children && user.children.length > 0;

  return (
    <div className={`uh-branch ${depth > 0 ? 'uh-branch-nested' : ''}`}>
      <div className="uh-branch-line-wrap">
        {depth > 0 && <div className="uh-connector" />}
        <UserCard user={user} isRoot={depth === 0} />
      </div>

      {hasChildren && (
        <div className="uh-children">
          {user.children!.map((child) => (
            <HierarchyBranch key={child.id} user={child} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
};

const UserHierarchy = ({ nextStep, previousStep }: Props) => {
  return (
    <div className="uh-card">
      {/* Header */}
      <div className="uh-header">
        <h2 className="uh-title">User Hierarchy</h2>
        <p className="uh-subtitle">Preview reporting lines and hierarchy</p>
        <p className="uh-desc">
          Review the user structure below — verify department assignments, reporting lines, and portal types before completing setup.
        </p>
      </div>

      {/* Department */}
      <div className="uh-department">
        <div className="uh-dept-icon">
          <Building2 size={18} />
        </div>
        <div className="uh-dept-text">
          <span className="uh-dept-name">IT & Security</span>
          <span className="uh-dept-count">5 users</span>
        </div>
      </div>

      {/* Hierarchy tree */}
      <div className="uh-tree">
        <HierarchyBranch user={HIERARCHY} depth={0} />
      </div>

      {/* Footer note */}
      <p className="uh-footer-note">
        Reporting manager assignments and org unit details can be updated from the Users module after setup is complete.
      </p>

      {/* Navigation */}
      <div className="uh-footer">
        <button type="button" onClick={previousStep} className="uh-back-btn">
          <ArrowLeft size={16} />
          Back
        </button>
        <button type="button" onClick={nextStep} className="uh-next-btn">
          Next
          <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
};

export default UserHierarchy;
